import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dealsAPI, teamAPI, dealStagesAPI, hrAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Eye, Trash2, ArrowUpDown, ChevronLeft, ChevronRight, Download, Bookmark, BookmarkPlus, Trophy, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '../contexts/CurrencyContext';
import { formatCurrency as formatCurrencyCode } from '../utils/currency';
import { TableSkeleton } from '../components/ui/loading-states';
import { EmptyState, SearchEmptyState } from '../components/ui/empty-states';
import { ErrorState } from '../components/ui/error-states';
import ViewDealOffcanvas from '../components/ViewDealOffcanvas';
import { HelpDot } from '../components/HelpDot';
import EditDealOffcanvas from '../components/EditDealOffcanvas';
import { useWebSocket, EventTypes, Channels } from '../hooks/useWebSocket';

const stageColors = {
  'Prospecting': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'Qualification': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'Proposal': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  'Negotiation': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  'Closed Won': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'Won': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'Closed Lost': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  'Lost': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const priorityColors = {
  'Low': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  'Medium': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  'High': 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  'Critical': 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const PAGE_SIZE = 20;

export default function Deals() {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dealStages, setDealStages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [totalByCurrency, setTotalByCurrency] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchDebounce, setSearchDebounce] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE, sort_by: sortBy, sort_order: sortOrder };
      if (stageFilter && stageFilter !== 'all') params.stage = stageFilter;
      if (priorityFilter && priorityFilter !== 'all') params.priority = priorityFilter;
      if (ownerFilter && ownerFilter !== 'all') params.assigned_to = ownerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (searchDebounce) params.search = searchDebounce;
      const response = await dealsAPI.getAll(params);
      const result = response.data;
      setDeals(result.data || []);
      setTotalCount(result.total || 0);
      setTotalByCurrency(Array.isArray(result.total_by_currency) ? result.total_by_currency : []);
      // Summary bar (same filters, minus pagination)
      const { skip, limit, ...summaryParams } = params;
      dealsAPI.getSummary(summaryParams).then(r => setSummary(r.data)).catch(() => setSummary(null));
    } catch (err) {
      console.error('Failed to load deals:', err);
      setError('Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, [stageFilter, priorityFilter, ownerFilter, dateFrom, dateTo, searchDebounce, page, sortBy, sortOrder]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const loadSavedViews = useCallback(async () => {
    try {
      const res = await dealsAPI.listViews();
      setSavedViews(Array.isArray(res.data) ? res.data : []);
    } catch { /* keep empty */ }
  }, []);
  useEffect(() => { loadSavedViews(); }, [loadSavedViews]);

  const applyView = (view) => {
    const f = view.filters || {};
    setStageFilter(f.stage || '');
    setPriorityFilter(f.priority || '');
    setOwnerFilter(f.assigned_to || '');
    setDateFrom(f.date_from || '');
    setDateTo(f.date_to || '');
    setSearchTerm(f.search || '');
    if (f.sort_by) setSortBy(f.sort_by);
    if (f.sort_order) setSortOrder(f.sort_order);
    toast.success(`Applied view "${view.name}"`);
  };

  const saveCurrentView = async () => {
    const name = window.prompt('Name this view (e.g. "This month · Won")');
    if (!name || !name.trim()) return;
    const filters = {};
    if (stageFilter && stageFilter !== 'all') filters.stage = stageFilter;
    if (priorityFilter && priorityFilter !== 'all') filters.priority = priorityFilter;
    if (ownerFilter && ownerFilter !== 'all') filters.assigned_to = ownerFilter;
    if (dateFrom) filters.date_from = dateFrom;
    if (dateTo) filters.date_to = dateTo;
    if (searchDebounce) filters.search = searchDebounce;
    filters.sort_by = sortBy;
    filters.sort_order = sortOrder;
    try {
      await dealsAPI.createView({ name: name.trim(), filters });
      toast.success('View saved');
      loadSavedViews();
    } catch {
      toast.error('Failed to save view');
    }
  };

  const deleteView = async (id, e) => {
    e?.stopPropagation();
    try {
      await dealsAPI.deleteView(id);
      loadSavedViews();
    } catch {
      toast.error('Failed to delete view');
    }
  };

  // Load deal stages + team members for the filter dropdowns
  useEffect(() => {
    (async () => {
      try {
        const res = await dealStagesAPI.getStages();
        setDealStages(Array.isArray(res.data) ? res.data : []);
      } catch { /* keep empty; UI falls back gracefully */ }
      try {
        const res = await hrAPI.getTeamMembers();
        const arr = res.data?.users || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setTeamMembers(arr.map(u => ({ id: u.id, name: u.name || u.email })));
      } catch { /* keep empty */ }
    })();
  }, []);

  const buildExportParams = () => {
    const params = new URLSearchParams({ sort_by: sortBy, sort_order: sortOrder });
    if (stageFilter && stageFilter !== 'all') params.set('stage', stageFilter);
    if (priorityFilter && priorityFilter !== 'all') params.set('priority', priorityFilter);
    if (ownerFilter && ownerFilter !== 'all') params.set('assigned_to', ownerFilter);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    if (searchDebounce) params.set('search', searchDebounce);
    return params;
  };

  const handleExport = async (fmt = 'csv') => {
    try {
      setExporting(true);
      const params = buildExportParams();
      params.set('format', fmt);
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deals/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fmt === 'xlsx' ? 'deals_export.xlsx' : 'deals_export.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Deals exported (${fmt === 'xlsx' ? 'Excel' : 'CSV'})`);
    } catch (e) {
      toast.error('Failed to export deals');
    } finally {
      setExporting(false);
    }
  };

  // Phase P4: Real-time Deals — when any teammate moves a deal stage or updates it, refresh silently.
  const wsRefreshTimerRef = useRef(null);
  const scheduleWsRefresh = useCallback(() => {
    if (wsRefreshTimerRef.current) clearTimeout(wsRefreshTimerRef.current);
    wsRefreshTimerRef.current = setTimeout(() => { fetchDeals(); }, 600);
  }, [fetchDeals]);
  useWebSocket(
    [Channels.SALES],
    {
      [EventTypes.DEAL_STAGE_CHANGED]: (data) => {
        scheduleWsRefresh();
        if (data?.deal_name && data?.stage && data?.previous_stage && data.stage !== data.previous_stage) {
          toast.info(`Deal "${data.deal_name}" moved to ${data.stage}`, { duration: 3000 });
        }
      },
      [EventTypes.DEAL_UPDATED]: scheduleWsRefresh,
      [EventTypes.DEAL_CREATED]: scheduleWsRefresh,
    },
    { showToasts: false, autoConnect: true }
  );

  // Reset page when filters change
  useEffect(() => { setPage(0); }, [stageFilter, priorityFilter, ownerFilter, dateFrom, dateTo, searchDebounce]);

  const handleDelete = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    try {
      await dealsAPI.delete(dealId);
      toast.success('Deal deleted');
      fetchDeals();
    } catch {
      toast.error('Failed to delete deal');
    }
  };

  const handleView = (deal) => {
    setSelectedDeal(deal);
    setViewOpen(true);
  };

  const handleEdit = (deal) => {
    setSelectedDeal(deal);
    setEditOpen(true);
    setViewOpen(false);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const formatCurrency = (amount, code) => {
    if (code) return formatCurrencyCode(amount || 0, code);
    return currency?.format ? currency.format(amount || 0) : `$${(amount || 0).toFixed(2)}`;
  };
  const formatByCurrency = (rows) =>
    (rows || []).map((r) => `${r.currency} ${formatCurrency(r.amount, r.currency)}`).join(' · ');

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const SortHeader = ({ column, children }) => (
    <button className="flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => handleSort(column)} data-testid={`sort-${column}`}>
      {children}
      <ArrowUpDown className={`h-3 w-3 ${sortBy === column ? 'text-primary' : 'text-muted-foreground'}`} />
    </button>
  );

  if (error) {
    return <ErrorState message={error} onRetry={fetchDeals} />;
  }

  return (
    <div className="space-y-6" data-testid="deals-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-1.5" data-testid="deals-title">Deals <HelpDot guide="build-pipeline" label="How the pipeline works" /></h1>
          <p className="text-sm text-muted-foreground">Manage and track your sales deals ({totalCount} total)</p>
        </div>
        <div className="rounded-lg border bg-muted/40 px-4 py-2 text-right" data-testid="deals-total-amount-card">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">
            Total Deal Amount{totalByCurrency.length > 1 && <span className="ml-1 normal-case text-amber-600 dark:text-amber-400" data-testid="deals-mixed-currency-badge">· per currency</span>}
          </p>
          {totalByCurrency.length === 0 ? (
            <p className="text-xl font-bold text-foreground" data-testid="deals-total-amount">{formatCurrency(0)}</p>
          ) : totalByCurrency.map((r) => (
            <p key={r.currency} className={`font-bold text-foreground ${totalByCurrency.length > 1 ? 'text-base' : 'text-xl'}`} data-testid={`deals-total-amount-${r.currency}`}>
              {totalByCurrency.length > 1 && <span className="mr-1.5 text-xs font-medium text-muted-foreground">{r.currency}</span>}
              <span data-testid={totalByCurrency.length === 1 ? 'deals-total-amount' : undefined}>{formatCurrency(r.amount, r.currency)}</span>
              {totalByCurrency.length > 1 && <span className="ml-1.5 text-xs font-normal text-muted-foreground">({r.count})</span>}
            </p>
          ))}
        </div>
      </div>

      {/* Summary Bar — mini dashboard */}
      {summary && (
        <div className="flex flex-wrap items-center gap-2" data-testid="deals-summary-bar">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold" data-testid="summary-total">
            {summary.total_count} deals
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-3 py-1 text-xs font-semibold" data-testid="summary-win-rate">
            <Trophy className="h-3.5 w-3.5" /> Win rate {summary.win_rate}%
            <span className="text-emerald-500/70 font-normal">({summary.won_count}W · {summary.lost_count}L)</span>
          </span>
          <span className="h-4 w-px bg-border mx-1" />
          {summary.by_stage.map((s) => (
            <button
              key={s.stage}
              onClick={() => setStageFilter(s.stage === stageFilter ? '' : s.stage)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${stageFilter === s.stage ? 'ring-2 ring-primary/40' : ''} ${stageColors[s.stage] || 'bg-muted text-muted-foreground'}`}
              data-testid={`summary-stage-${s.stage}`}
              title={`${s.stage}: ${formatByCurrency(s.by_currency)}`}
            >
              {s.stage} <span className="font-bold">{s.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Saved Views */}
      <div className="flex flex-wrap items-center gap-2" data-testid="deals-saved-views">
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Bookmark className="h-3.5 w-3.5" /> Views:</span>
        {savedViews.length === 0 && <span className="text-xs text-muted-foreground/70">No saved views yet</span>}
        {savedViews.map((v) => (
          <span
            key={v.id}
            className="group inline-flex items-center gap-1.5 rounded-full bg-secondary text-secondary-foreground pl-3 pr-1.5 py-1 text-xs font-medium cursor-pointer hover:bg-secondary/80"
            onClick={() => applyView(v)}
            data-testid={`saved-view-${v.id}`}
          >
            {v.name}
            <button onClick={(e) => deleteView(v.id, e)} className="rounded-full hover:bg-background/60 p-0.5" data-testid={`delete-view-${v.id}`} title="Delete view">
              <XIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
        <Button variant="ghost" size="sm" onClick={saveCurrentView} className="gap-1.5 h-7 text-xs" data-testid="save-view-btn">
          <BookmarkPlus className="h-3.5 w-3.5" /> Save current view
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="deals-search"
            placeholder="Search deals..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">From Date</label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch (_) {} }}
            className="w-[150px] cursor-pointer dark:[color-scheme:dark]"
            data-testid="deals-date-from"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">To Date</label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch (_) {} }}
            className="w-[150px] cursor-pointer dark:[color-scheme:dark]"
            data-testid="deals-date-to"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[160px]" data-testid="deals-stage-filter">
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {(dealStages.length ? dealStages.map(s => s.name) : ['Prospecting','Qualification','Proposal','Negotiation','Closed Won','Closed Lost']).map((name) => (
              <SelectItem key={name} value={name}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="w-[160px]" data-testid="deals-owner-filter">
            <SelectValue placeholder="All Owners" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Owners</SelectItem>
            {teamMembers.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[140px]" data-testid="deals-priority-filter">
            <SelectValue placeholder="All Priorities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Critical">Critical</SelectItem>
          </SelectContent>
        </Select>
        {(searchTerm || stageFilter || priorityFilter || ownerFilter || dateFrom || dateTo) && (
          <Button
            variant="ghost"
            onClick={() => { setSearchTerm(''); setStageFilter(''); setPriorityFilter(''); setOwnerFilter(''); setDateFrom(''); setDateTo(''); }}
            data-testid="deals-clear-filters"
          >
            Clear
          </Button>
        )}
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={() => handleExport('csv')} disabled={exporting} data-testid="deals-export-csv-btn" className="gap-2">
            <Download className="h-4 w-4" /> CSV
          </Button>
          <Button onClick={() => handleExport('xlsx')} disabled={exporting} data-testid="deals-export-excel-btn" className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
            <Download className="h-4 w-4" /> Export Excel
          </Button>
        </div>
      </div>

      {/* Deals Table */}
      {loading ? (
        <TableSkeleton rows={5} columns={8} />
      ) : deals.length === 0 ? (
        searchTerm || (stageFilter && stageFilter !== 'all') || (priorityFilter && priorityFilter !== 'all') || (ownerFilter && ownerFilter !== 'all') || dateFrom || dateTo ? (
          <SearchEmptyState searchTerm={searchTerm || stageFilter || priorityFilter} onClear={() => { setSearchTerm(''); setStageFilter(''); setPriorityFilter(''); setOwnerFilter(''); setDateFrom(''); setDateTo(''); }} />
        ) : (
          <EmptyState
            title="No deals found"
            description="Deals are created from lead details. Go to a lead and create a deal."
          />
        )
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">S.No</TableHead>
                  <TableHead><SortHeader column="deal_name">Deal Name</SortHeader></TableHead>
                  <TableHead>Lead</TableHead>
                  <TableHead><SortHeader column="stage">Stage</SortHeader></TableHead>
                  <TableHead><SortHeader column="priority">Priority</SortHeader></TableHead>
                  <TableHead><SortHeader column="total">Amount</SortHeader></TableHead>
                  <TableHead><SortHeader column="expected_close_date">Expected Close</SortHeader></TableHead>
                  <TableHead><SortHeader column="created_at">Created</SortHeader></TableHead>
                  <TableHead><SortHeader column="updated_at">Modified</SortHeader></TableHead>
                  <TableHead>Products / Services</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deals.map((deal, idx) => (
                  <TableRow key={deal.id} data-testid={`deal-row-${deal.id}`}>
                    <TableCell>{page * PAGE_SIZE + idx + 1}</TableCell>
                    <TableCell>
                      <button
                        className="text-primary hover:underline font-medium"
                        onClick={() => navigate(`/deals/${deal.id}`)}
                        data-testid={`deal-name-${deal.id}`}
                      >
                        {deal.deal_name || `Deal #${deal.id.slice(0, 8)}`}
                      </button>
                    </TableCell>
                    <TableCell>
                      {deal.lead_name ? (
                        <button className="text-primary hover:underline" onClick={() => navigate(`/leads/${deal.lead_id}`)}>
                          {deal.lead_name}
                        </button>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={stageColors[deal.stage] || 'bg-muted text-muted-foreground'}>
                        {deal.stage}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={priorityColors[deal.priority] || ''}>
                        {deal.priority || 'Medium'}
                      </Badge>
                    </TableCell>
                    <TableCell data-testid={`deal-total-${deal.id}`}>{formatCurrency(deal.total, deal.currency)}</TableCell>
                    <TableCell>{formatDate(deal.expected_close_date)}</TableCell>
                    <TableCell>{formatDate(deal.created_at)}</TableCell>
                    <TableCell>{formatDate(deal.updated_at)}</TableCell>
                    <TableCell>
                      {(() => {
                        const items = deal.line_items || [];
                        if (items.length === 0) return <span className="text-muted-foreground">—</span>;
                        const names = items.map((it) => it.description || it.name || 'Item');
                        const extra = names.length - 1;
                        return (
                          <span className="block max-w-[180px] truncate" title={names.join(', ')} data-testid={`deal-products-${deal.id}`}>
                            {names[0]}{extra > 0 ? ` +${extra}` : ''}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/deals/${deal.id}`)} data-testid={`deal-view-${deal.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(deal.id)} data-testid={`deal-delete-${deal.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between" data-testid="deals-pagination">
              <p className="text-sm text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalCount)} of {totalCount}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="deals-prev-page">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <span className="text-sm font-medium px-2">Page {page + 1} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="deals-next-page">
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ViewDealOffcanvas
        isOpen={viewOpen}
        onClose={() => { setViewOpen(false); setSelectedDeal(null); }}
        deal={selectedDeal}
        onEdit={handleEdit}
        onDelete={fetchDeals}
        onConvertToQuote={fetchDeals}
      />

      <EditDealOffcanvas
        isOpen={editOpen}
        onClose={() => { setEditOpen(false); setSelectedDeal(null); }}
        deal={selectedDeal}
        onSuccess={() => { setEditOpen(false); fetchDeals(); }}
      />
    </div>
  );
}
